import { useState } from 'react';
import { Group, Box, Collapse, ThemeIcon, Text, UnstyledButton, rem } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { useHistory, useLocation } from 'react-router-dom';
import classes from './NavbarLinksGroup.module.css';
import { useMediaQuery } from '@mantine/hooks';

export interface LinkItem {
  label: string;
  link: string;
  className?: string;
  hiddenFrom?: string;
  isHeader?: boolean;
}

export interface LinksGroupProps {
  icon: unknown;
  label: string;
  initiallyOpened?: boolean;
  links?: LinkItem[];
  link?: string;
  className?: string;
  hiddenFrom?: string;
  onClick?: () => void;
  isActive?: boolean;
  childLinksURL?: string[];
  toggleMenu?: () => void;
}

interface SubSection {
  headerLabel: string;
  hiddenFrom?: string;
  links: LinkItem[];
}

type NavbarIconProps = {
  className?: string;
  size?: number;
  stroke?: number;
  style?: React.CSSProperties;
};

type NavbarIconComponent = React.FC<NavbarIconProps>;

const ChevronRightIcon = IconChevronRight as unknown as NavbarIconComponent;

/**
 * Groups a flat links array into subsections.
 * Items before the first isHeader are "ungrouped" (rendered directly).
 * Items after an isHeader belong to that subsection (collapsible).
 */
function groupLinksIntoSubSections(links: LinkItem[]): { ungrouped: LinkItem[]; sections: SubSection[] } {
  const ungrouped: LinkItem[] = [];
  const sections: SubSection[] = [];
  let current: SubSection | null = null;

  for (const item of links) {
    if (item.isHeader) {
      if (current) sections.push(current);
      current = { headerLabel: item.label, hiddenFrom: item.hiddenFrom, links: [] };
    } else if (current) {
      current.links.push(item);
    } else {
      ungrouped.push(item);
    }
  }
  if (current) sections.push(current);

  return { ungrouped, sections };
}

const buildNavigationTarget = (rawLink = '', storeId: string | null) => {
  const [pathAndSearch, hash] = rawLink.split('#');
  const queryStart = pathAndSearch.indexOf('?');
  const pathname =
    queryStart >= 0 ? pathAndSearch.slice(0, queryStart) || '/' : pathAndSearch || '/';
  const params = new URLSearchParams(queryStart >= 0 ? pathAndSearch.slice(queryStart + 1) : '');

  if (storeId !== null) {
    params.set('storeId', storeId);
  }

  const search = params.toString();

  return {
    pathname,
    search: search ? `?${search}` : '',
    hash: hash ? `#${hash}` : undefined
  };
};

function SubSectionGroup({
  section,
  pathname,
  searchParamStoreId,
  history,
  onNavigate
}: {
  section: SubSection;
  pathname: string;
  searchParamStoreId: string | null;
  history: ReturnType<typeof useHistory>;
  onNavigate: () => void;
}) {
  const [opened, setOpened] = useState(false);

  return (
    <Box hiddenFrom={section.hiddenFrom || ''}>
      <UnstyledButton
        onClick={() => setOpened((o) => !o)}
        style={{ width: '100%' }}>
        <Group
          gap={4}
          pl="xl"
          pr="md"
          py={4}
          mt="xs"
          style={{ cursor: 'pointer' }}>
          <ChevronRightIcon
            size={12}
            stroke={2}
            style={{
              color: 'var(--mantine-color-dimmed)',
              transition: 'transform 200ms ease',
              transform: opened ? 'rotate(90deg)' : 'none'
            }}
          />
          <Text
            size="xs"
            fw={700}
            tt="uppercase"
            c="dimmed">
            {section.headerLabel}
          </Text>
        </Group>
      </UnstyledButton>
      <Collapse in={opened}>
        {section.links.map((link) => (
          <Text<'a'>
            component="a"
            data-active={link.link === pathname}
            className={classes.link}
            key={link.label}
            onClick={(event) => {
              event.preventDefault();
              history.push(buildNavigationTarget(link.link, searchParamStoreId));
              onNavigate();
            }}
            hiddenFrom={link.hiddenFrom || ''}>
            {link.label}
          </Text>
        ))}
      </Collapse>
    </Box>
  );
}

export function LinksGroup({
  icon: Icon,
  label,
  initiallyOpened,
  links,
  link,
  hiddenFrom,
  onClick,
  isActive = false,
  toggleMenu
}: LinksGroupProps) {
  const hasLinks = Array.isArray(links);
  const history = useHistory();
  const location = useLocation();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId: string | null = searchParams.get('storeId');
  const IconComponent = Icon as NavbarIconComponent;

  const [opened, setOpened] = useState(initiallyOpened || false);

  const { ungrouped, sections } = hasLinks
    ? groupLinksIntoSubSections(links)
    : { ungrouped: [], sections: [] };

  const hasSections = sections.length > 0;
  const closeMobileMenuAfterNavigation = () => {
    if (!isDesktop) {
      toggleMenu?.();
    }
  };

  const renderLink = (linkItem: LinkItem) => (
    <Text<'a'>
      component="a"
      data-active={linkItem.link === location.pathname}
      className={classes.link}
      key={linkItem.label}
      onClick={(event) => {
        event.preventDefault();
        history.push(buildNavigationTarget(linkItem.link, searchParamStoreId));
        closeMobileMenuAfterNavigation();
      }}
      hiddenFrom={linkItem.hiddenFrom || ''}>
      {linkItem.label}
    </Text>
  );

  const toggleSubmenu = () => {
    setOpened((o) => !o);
  };
  const navigateToPage = () => {
    closeMobileMenuAfterNavigation();
    history.push({
      ...buildNavigationTarget(link, searchParamStoreId),
      state: { storeID: searchParamStoreId }
    });
  };
  const onOptionClick = () => {
    if (typeof onClick === 'function') {
      return onClick();
    }
    return hasLinks ? toggleSubmenu() : navigateToPage();
  };

  return (
    <>
      <UnstyledButton
        onClick={onOptionClick}
        data-active={isActive || undefined}
        className={`${classes.control}`}
        hiddenFrom={hiddenFrom ? hiddenFrom : ''}
        key={label}>
        <Group justify="space-between" gap={0}>
          <Box style={{ display: 'flex', alignItems: 'center' }} className="box-container-links">
            <ThemeIcon variant="light" size={30}>
              <IconComponent style={{ width: rem(18), height: rem(18) }} />
            </ThemeIcon>
            <Box ml="md">{label}</Box>
          </Box>
          {hasLinks && (
            <ChevronRightIcon
              className={classes.chevron}
              stroke={1.5}
              style={{
                width: rem(16),
                height: rem(16),
                transform: opened ? 'rotate(-90deg)' : 'none'
              }}
            />
          )}
        </Group>
      </UnstyledButton>
      {hasLinks ? (
        <Collapse in={opened}>
          {ungrouped.map(renderLink)}
          {hasSections &&
            sections.map((section) => (
              <SubSectionGroup
                key={section.headerLabel}
                section={section}
                pathname={location.pathname}
                searchParamStoreId={searchParamStoreId}
                history={history}
                onNavigate={closeMobileMenuAfterNavigation}
              />
            ))}
        </Collapse>
      ) : null}
    </>
  );
}
